import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dropdown, Form, Icon, Input } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { StyledFormSection, StyledBasicInfoContainer, StyledFieldsContainer } from '../style'
import AvatarUpload from './AvatarUpload'
import { ICountry } from '~/api/services/dairy'
import { DateInput } from 'semantic-ui-calendar-react'
import { useAuth } from '~/routes/components/AuthProvider'
import { getError } from '~/components/FormFields/util'

interface Props {
  farmerGroupList: Array<{ value: number; text: string }>
  isLoadingFarmerGrouplist: boolean
  countries: ICountry[]
}

const BasicInformation: React.FC<Props> = ({ farmerGroupList, isLoadingFarmerGrouplist, countries }) => {
  const { t } = useTranslation()
  const { control, setValue, formState: { isSubmitted } } = useFormContext()
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)

  const wathcMobileCodeField = useWatch({ control, name: 'mobilePrefix' })
  const mobileNumber = useWatch({ control, name: 'mobileNumber' })
  const watchDate = useWatch({ control, name: 'joiningDate' })

  return (
    <StyledFormSection>
      <div className='section-header'>
        <Icon name='building' />
        <span className='section-title'>{t('Basic Information about operation')}</span>
      </div>
      <div className='section-content'>
        <StyledBasicInfoContainer>
          <AvatarUpload />
          <StyledFieldsContainer>
            <Form.Group widths={'equal'}>
              <FormFields.Input
                control={control}
                name={'evsNumber'}
                placeholder={t('EVS number')}
                rules={{
                  required: t('Please enter evs')
                }}
                label={t('EVS number')}
              />
              <FormFields.Input
                control={control}
                name={'supplierNo'}
                placeholder={t('Suplier no.')}
                label={t('Suplier no.')}
              />
              <FormFields.Input
                control={control}
                name={'agisId'}
                placeholder={t('AGIS-ID Operation')}
                rules={{
                  required: t('Please enter agis-id')
                }}
                label={t('AGIS-ID Operation')}
              />
            </Form.Group>

            <Form.Group widths={'equal'}>
              <FormFields.Select
                selectOnBlur={false}
                control={control}
                label={t('Farmer group')}
                disabled={isLoadingFarmerGrouplist}
                loading={isLoadingFarmerGrouplist}
                options={farmerGroupList}
                name='farmerGroupId'
                placeholder={t('Select farmer group')}
                rules={{
                  required: {
                    value: true,
                    message: t('Select farmer group')
                  }
                }}
              />
              <FormFields.Input
                control={control}
                name={'companyName'}
                rules={{
                  required: {
                    value: true,
                    message: t('Plese enter company name')
                  }
                }}
                placeholder={t('Company Name')}
                label={t('Company Name')}
              />

              <Form.Field style={{ paddingLeft: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <Icon name={'mobile alternate'} style={{ fontSize: '1.2em' }} />
                  <div>{t('Mobile number')}</div>
                </label>
                <Input
                  value={mobileNumber || ''}
                  label={<Dropdown
                    scrolling
                    basic
                    clearable
                    options={countries.map(countryCode => ({ value: countryCode.id, text: countryCode.phoneCode, flag: countryCode.iso }))}
                    selectOnBlur={false}
                    value={parseInt(wathcMobileCodeField) || ''}
                    onChange={(e, data) => setValue('mobilePrefix', data.value)}
                    placeholder={t('Code')}
                    name={'mobilePrefix'}
                  />}
                  onChange={(e, data) => setValue('mobileNumber', data.value)}
                  labelPosition='left'
                  style={{ marginBottom: 10 }}
                  type={'number'}
                  placeholder={wathcMobileCodeField ? t('Mobile number') : t('Please select mobile code')}
                  name={'mobileNumber'}
                />
              </Form.Field>
            </Form.Group>

            <Form.Group widths={'equal'} style={{ alignItems: 'center' }}>
              <FormFields.Checkbox
                control={control}
                defaultValue={0}
                style={{ marginTop: 10 }}
                checkboxValue={1}
                name={'deliveryStop'}
                label={t('Delivery stop')}
              />
              <Controller
                control={control}
                name={'joiningDate'}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter date')
                  }
                }}
                render={({ field, fieldState }) => {
                  return <Form.Field
                    required
                    error={!watchDate && isSubmitted ? getError(fieldState) : false}
                  >
                    <label>{t('Date joined')}</label>
                    <DateInput
                      name='date'
                      placeholder={t('Date joined')}
                      closable
                      markColor={'blue'}
                      localization={languageLocal.code}
                      closeOnMouseLeave={false}
                      dateFormat={'DD.MM.YYYY'}
                      className={'datePicker'}
                      value={watchDate}
                      iconPosition='left'
                      onChange={(event, { name, value }) => setValue('joiningDate', value)}
                    />
                  </Form.Field>

                }}
              />
              <Form.Field />
            </Form.Group>
          </StyledFieldsContainer>
        </StyledBasicInfoContainer>
      </div>
    </StyledFormSection>
  )
}

export default BasicInformation
